import React from "react";
import { useNavigate } from "react-router-dom";

import AppSidebarV2 from "../components/layout/AppSidebarV2";
import Modal from "../components/ui/Modal";
import { useAuth } from "../auth/authStore";
import { financeApi } from "../api/financeApi";
import { profileApi } from "../api/profileApi";
import { useToast } from "../ui/toast/ToastProvider";

const PSYCHOLOGIST_ROLES = new Set(["PSYCHOLOGIST", "PSYCHO"]);
const PAYMENT_METHOD_OPTIONS = [
  { value: "bank_card", label: "Перевод на карту" },
  { value: "sbp", label: "СБП" },
  { value: "cash", label: "Наличные" },
  { value: "payment_link", label: "Ссылка на оплату" },
  { value: "self_employed", label: "Самозанятость / чек" },
];
const PAYMENT_TIMING_OPTIONS = [
  "До записи",
  "После подтверждения записи",
  "После консультации",
  "По договорённости",
];
const POLICY_PRESETS = [
  {
    id: "soft",
    title: "Мягкая — за 12 часов",
    description: "Можно отменить или перенести за 12 часов",
    text: "Отменить или перенести сессию можно бесплатно не позднее чем за 12 часов до начала.",
  },
  {
    id: "standard",
    title: "Стандартная — за 24 часа",
    description: "Рекомендуемый вариант",
    text: "Отменить или перенести сессию можно бесплатно не позднее чем за 24 часа до начала.",
  },
  {
    id: "custom",
    title: "Своя формулировка",
    description: "Для индивидуальных правил работы",
    text: "",
  },
];
const TEAM_TAB_IDS = ["profile-payment-settings", "client-flow", "how-it-works", "tech-notes", "states"];
const PSY_TAB_IDS = ["overview", "needs-action", "history", "empty-payments"];

function normalizeRole(raw) {
  return String(raw || "")
    .replace(/^ROLE_/, "")
    .trim()
    .toUpperCase();
}

function formatMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0 ₽";
  return `${new Intl.NumberFormat("ru-RU").format(Math.round(amount))} ₽`;
}

function formatDate(value, options = { day: "2-digit", month: "2-digit", year: "numeric" }) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", options).format(date);
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function monthKey(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function paymentStatusMeta(status, amountDue = 0, amountPaid = 0) {
  const code = String(status || "").toUpperCase();
  if (code === "PAID") return { label: "Оплачено", tone: "paid" };
  if (code === "PENDING_CONFIRMATION") return { label: "Ожидает подтв.", tone: "pending" };
  if (code === "PARTIAL") return { label: `Частично${amountPaid > 0 ? ` · ${formatMoney(amountPaid)}` : ""}`, tone: "partial" };
  if (code === "NOT_REQUIRED") return { label: "Не требуется", tone: "notrequired" };
  if (code === "UNPAID") return { label: amountDue > 0 ? "Не оплачено" : "Без оплаты", tone: "unpaid" };
  return { label: "Не оплачено", tone: "unpaid" };
}

function sessionStatusMeta(status) {
  const code = String(status || "").toUpperCase();
  if (code === "COMPLETED") return { label: "Проведена", tone: "done" };
  if (code === "PAID") return { label: "Оплачена", tone: "done" };
  if (code === "NO_SHOW") return { label: "Неявка", tone: "partial" };
  if (code === "CANCELLED") return { label: "Отменена", tone: "unpaid" };
  if (code === "PENDING_PAYMENT") return { label: "Запланирована", tone: "planned" };
  if (code === "OPEN") return { label: "Открыта", tone: "planned" };
  return { label: code || "—", tone: "planned" };
}

function initials(value) {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) return "БД";
  return parts.map((part) => part[0]).join("").toUpperCase();
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function createPaymentForm(item) {
  return {
    bookingId: item?.bookingId ?? null,
    bookingMemberId: item?.bookingMemberId ?? null,
    clientUserId: item?.clientUserId ?? null,
    paymentStatus: String(item?.paymentStatus || "UNPAID").toUpperCase(),
    amountPaid: String(item?.amountPaid ?? item?.amountDue ?? 0),
    paymentMethod: String(item?.paymentMethod || "bank_card"),
    paymentDate: item?.paymentDate ? String(item.paymentDate).slice(0, 10) : "",
    comment: String(item?.comment || ""),
  };
}

function createPaymentSettingsForm(profile = {}) {
  return {
    pricePerSession: String(profile.pricePerSession ?? ""),
    paymentMethods: Array.isArray(profile.paymentMethods) ? profile.paymentMethods.map((item) => String(item)) : [],
    paymentTimingLabel: String(profile.paymentTimingLabel || ""),
    paymentInstructions: String(profile.paymentInstructions || ""),
    paymentCancellationPolicy: String(profile.paymentCancellationPolicy || ""),
    paymentSelfEmployed: Boolean(profile.paymentSelfEmployed),
  };
}

function paymentMethodLabel(value) {
  const token = String(value || "").trim();
  if (!token) return "";
  const option = PAYMENT_METHOD_OPTIONS.find((item) => item.value === token);
  return option?.label || token;
}

function moneyLabel(value, fallback = "Не указано") {
  if (value === null || value === undefined || value === "") return fallback;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return fallback;
  return formatMoney(amount);
}

function resolvePolicyPreset(value) {
  const text = String(value || "").toLowerCase();
  if (text.includes("12")) return "soft";
  if (text.includes("24")) return "standard";
  return "custom";
}

function FinanceBadge({ status, amountDue, amountPaid, type = "payment" }) {
  const meta = type === "payment" ? paymentStatusMeta(status, amountDue, amountPaid) : sessionStatusMeta(status);
  return (
    <span className={`finance-badge is-${meta.tone}`}>
      <span className="finance-badge__dot" />
      {meta.label}
    </span>
  );
}

export default function Finance() {
  const { me, role } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const roleToken = normalizeRole(role || me?.role);
  const isPsychologist = PSYCHOLOGIST_ROLES.has(roleToken);
  const canUseTeamMode = !isPsychologist;
  const canUsePsychologistMode = true;

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [settingsSaving, setSettingsSaving] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [profile, setProfile] = React.useState(null);
  const [paymentSettingsForm, setPaymentSettingsForm] = React.useState(() => createPaymentSettingsForm({}));
  const [mode, setMode] = React.useState("psychologist");
  const [psyTab, setPsyTab] = React.useState("overview");
  const [teamTab, setTeamTab] = React.useState("profile-payment-settings");
  const [periodFilter, setPeriodFilter] = React.useState("current");
  const [paymentStatusFilter, setPaymentStatusFilter] = React.useState("ALL");
  const [sessionStatusFilter, setSessionStatusFilter] = React.useState("ALL");
  const [methodFilter, setMethodFilter] = React.useState("ALL");
  const [query, setQuery] = React.useState("");
  const [modalItem, setModalItem] = React.useState(null);
  const [paymentForm, setPaymentForm] = React.useState(() => createPaymentForm(null));
  const activePolicyPreset = React.useMemo(
    () => resolvePolicyPreset(paymentSettingsForm.paymentCancellationPolicy),
    [paymentSettingsForm.paymentCancellationPolicy]
  );
  const selectedPaymentMethod = paymentSettingsForm.paymentMethods[0] || "";
  const previewPriceValue = paymentSettingsForm.pricePerSession || profile?.pricePerSession || "";
  const paymentTimingOptions = React.useMemo(() => {
    const options = [""];
    PAYMENT_TIMING_OPTIONS.forEach((option) => {
      if (!options.includes(option)) options.push(option);
    });
    if (paymentSettingsForm.paymentTimingLabel && !options.includes(paymentSettingsForm.paymentTimingLabel)) {
      options.push(paymentSettingsForm.paymentTimingLabel);
    }
    return options;
  }, [paymentSettingsForm.paymentTimingLabel]);

  React.useEffect(() => {
    setMode("psychologist");
  }, [isPsychologist]);

  React.useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      try {
        if (!isPsychologist) {
          if (ignore) return;
          setItems([]);
          setProfile(null);
          setPaymentSettingsForm(createPaymentSettingsForm({}));
          return;
        }
        const [financeItems, psychologistProfile] = await Promise.all([
          isPsychologist ? financeApi.listMine() : Promise.resolve([]),
          isPsychologist ? profileApi.getMyPsychologistProfile() : Promise.resolve(null),
        ]);
        if (ignore) return;
        setItems(Array.isArray(financeItems) ? financeItems : []);
        setProfile(psychologistProfile);
        setPaymentSettingsForm(createPaymentSettingsForm(psychologistProfile || {}));
      } catch (error) {
        if (!ignore) {
          toast.error(error?.message || "Не удалось загрузить раздел финансов");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [isPsychologist, toast]);

  const filteredHistoryItems = React.useMemo(() => {
    const now = new Date();
    const currentMonth = monthKey(now);
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonth = monthKey(lastMonthDate);
    const q = String(query || "").trim().toLowerCase();

    return items.filter((item) => {
      const itemDate = item.startAt ? new Date(item.startAt) : null;
      const itemMonth = itemDate && !Number.isNaN(itemDate.getTime()) ? monthKey(itemDate) : "";
      if (periodFilter === "current" && itemMonth !== currentMonth) return false;
      if (periodFilter === "last" && itemMonth !== lastMonth) return false;
      if (paymentStatusFilter !== "ALL" && item.paymentStatus !== paymentStatusFilter) return false;
      if (sessionStatusFilter !== "ALL" && item.sessionStatus !== sessionStatusFilter) return false;
      if (methodFilter !== "ALL" && String(item.paymentMethod || "") !== methodFilter) return false;
      if (q) {
        const haystack = [
          item.clientName,
          item.sessionTitle,
          item.comment,
          item.paymentMethod,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [items, methodFilter, paymentStatusFilter, periodFilter, query, sessionStatusFilter]);

  const needsActionItems = React.useMemo(
    () => filteredHistoryItems.filter((item) => item.needsAction),
    [filteredHistoryItems]
  );

  const summary = React.useMemo(() => {
    return filteredHistoryItems.reduce(
      (acc, item) => {
        const due = Number(item.amountDue) || 0;
        const paid = Number(item.amountPaid) || 0;
        if (item.paymentStatus === "PAID") {
          acc.paid += paid || due;
          acc.paidCount += 1;
        } else if (item.paymentStatus === "PENDING_CONFIRMATION") {
          acc.pending += due || paid;
          acc.pendingCount += 1;
        } else if (item.paymentStatus === "UNPAID") {
          acc.unpaid += Math.max(due - paid, due || 0);
          acc.unpaidCount += 1;
        } else if (item.paymentStatus === "PARTIAL") {
          acc.partial += Math.max(due - paid, 0);
          acc.partialCount += 1;
        }
        return acc;
      },
      { paid: 0, pending: 0, unpaid: 0, partial: 0, paidCount: 0, pendingCount: 0, unpaidCount: 0, partialCount: 0 }
    );
  }, [filteredHistoryItems]);

  const latestPaidItems = React.useMemo(
    () =>
      filteredHistoryItems
        .filter((item) => item.paymentStatus === "PAID")
        .slice(0, 3),
    [filteredHistoryItems]
  );

  const openPaymentModal = React.useCallback((item) => {
    setModalItem(item);
    setPaymentForm(createPaymentForm(item));
  }, []);

  const closePaymentModal = React.useCallback(() => {
    if (saving) return;
    setModalItem(null);
    setPaymentForm(createPaymentForm(null));
  }, [saving]);

  const handleExport = React.useCallback((exportItems) => {
    const rows = [
      [
        "Дата",
        "Клиент",
        "Тип",
        "Статус сессии",
        "Стоимость",
        "Оплата",
        "Способ",
        "Комментарий",
      ],
      ...exportItems.map((item) => [
        formatDateTime(item.startAt),
        item.clientName,
        item.bookingType === "GROUP" ? "Групповая" : "Индивидуальная",
        sessionStatusMeta(item.sessionStatus).label,
        formatMoney(item.amountDue),
        paymentStatusMeta(item.paymentStatus, item.amountDue, item.amountPaid).label,
        paymentMethodLabel(item.paymentMethod) || "—",
        item.comment || "—",
      ]),
    ];
    downloadCsv(`burosoul-finance-${new Date().toISOString().slice(0, 10)}.csv`, rows);
    toast.success("CSV выгружен");
  }, [toast]);

  const savePayment = React.useCallback(async () => {
    if (!modalItem?.bookingId) return;
    setSaving(true);
    try {
      const updated = await financeApi.updateBooking(modalItem.bookingId, {
        bookingMemberId: paymentForm.bookingMemberId,
        clientUserId: paymentForm.clientUserId,
        paymentStatus: paymentForm.paymentStatus,
        amountPaid: Number(paymentForm.amountPaid || 0),
        paymentMethod: paymentForm.paymentMethod,
        paymentDate: paymentForm.paymentDate ? `${paymentForm.paymentDate}T12:00:00+03:00` : null,
        comment: paymentForm.comment,
      });
      setItems((prev) =>
        prev.map((item) => {
          const sameBooking = item.bookingId === updated.bookingId;
          const sameMember = (item.bookingMemberId || null) === (updated.bookingMemberId || null);
          const sameClient = (item.clientUserId || null) === (updated.clientUserId || null);
          return sameBooking && sameMember && sameClient ? updated : item;
        })
      );
      toast.success("Оплата сохранена");
      closePaymentModal();
    } catch (error) {
      toast.error(error?.message || "Не удалось сохранить оплату");
    } finally {
      setSaving(false);
    }
  }, [closePaymentModal, modalItem?.bookingId, paymentForm, toast]);

  const savePaymentSettings = React.useCallback(async () => {
    if (!isPsychologist) return;
    setSettingsSaving(true);
    try {
      const updated = await profileApi.updateMyPsychologistProfile({
        ...(profile || {}),
        paymentMethods: paymentSettingsForm.paymentMethods,
        paymentTimingLabel: paymentSettingsForm.paymentTimingLabel,
        paymentInstructions: paymentSettingsForm.paymentInstructions,
        paymentCancellationPolicy: paymentSettingsForm.paymentCancellationPolicy,
        paymentSelfEmployed: paymentSettingsForm.paymentSelfEmployed,
        pricePerSession: paymentSettingsForm.pricePerSession ? Number(paymentSettingsForm.pricePerSession) : null,
      });
      setProfile(updated);
      setPaymentSettingsForm(createPaymentSettingsForm(updated || {}));
      toast.success("Настройки оплаты сохранены");
    } catch (error) {
      toast.error(error?.message || "Не удалось сохранить настройки оплаты");
    } finally {
      setSettingsSaving(false);
    }
  }, [isPsychologist, paymentSettingsForm, profile, toast]);

  const applyPolicyPreset = React.useCallback((presetId) => {
    const preset = POLICY_PRESETS.find((item) => item.id === presetId);
    setPaymentSettingsForm((prev) => ({
      ...prev,
      paymentCancellationPolicy:
        presetId === "custom"
          ? (resolvePolicyPreset(prev.paymentCancellationPolicy) === "custom" ? prev.paymentCancellationPolicy : "")
          : (preset?.text || prev.paymentCancellationPolicy),
    }));
  }, []);

  const copyPaymentInstructions = React.useCallback(async () => {
    const text = String(paymentSettingsForm.paymentInstructions || "").trim();
    if (!text) {
      toast.error("Реквизиты не указаны");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Реквизиты скопированы");
    } catch {
      toast.error("Не удалось скопировать реквизиты");
    }
  }, [paymentSettingsForm.paymentInstructions, toast]);

  const renderSummaryStrip = () => (
    <div className="finance-shell__summary-strip">
      <div className="finance-shell__summary-card">
        <div className="finance-shell__summary-label">Оплачено за период</div>
        <div className="finance-shell__summary-value is-mint">{formatMoney(summary.paid)}</div>
        <div className="finance-shell__summary-note">{summary.paidCount} записей</div>
      </div>
      <div className="finance-shell__summary-card">
        <div className="finance-shell__summary-label">Ждёт подтверждения</div>
        <div className="finance-shell__summary-value is-lav">{formatMoney(summary.pending)}</div>
        <div className="finance-shell__summary-note">{summary.pendingCount} клиентов сообщили об оплате</div>
      </div>
      <div className="finance-shell__summary-card">
        <div className="finance-shell__summary-label">Не оплачено</div>
        <div className="finance-shell__summary-value is-red">{formatMoney(summary.unpaid)}</div>
        <div className="finance-shell__summary-note">{summary.unpaidCount} записей требуют внимания</div>
      </div>
      <div className="finance-shell__summary-card">
        <div className="finance-shell__summary-label">Частично оплачено</div>
        <div className="finance-shell__summary-value is-amber">{formatMoney(summary.partial)}</div>
        <div className="finance-shell__summary-note">{summary.partialCount} записей с остатком</div>
      </div>
    </div>
  );

  const renderHistoryTable = (tableItems, compact = false) => (
    <div className="finance-shell__table-card">
      <table className="finance-shell__table">
        <thead>
          <tr>
            <th>Дата</th>
            <th>Клиент</th>
            <th>Тип</th>
            <th>Статус сессии</th>
            <th>Стоимость</th>
            <th>Оплата</th>
            <th>Способ</th>
            <th>Комментарий</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tableItems.map((item) => (
            <tr key={`${item.bookingId}-${item.bookingMemberId || "direct"}-${item.clientUserId}`}>
              <td data-label="Дата">{formatDateTime(item.startAt)}</td>
              <td data-label="Клиент">
                <div className="finance-shell__client-cell">
                  <div className="finance-shell__avatar">{initials(item.clientName)}</div>
                  <div>
                    <div className="finance-shell__client-name">{item.clientName}</div>
                    <div className="finance-shell__client-meta">{item.sessionTitle}</div>
                  </div>
                </div>
              </td>
              <td data-label="Тип">{item.bookingType === "GROUP" ? "Групповая" : "Индивид."}</td>
              <td data-label="Статус сессии"><FinanceBadge status={item.sessionStatus} type="session" /></td>
              <td data-label="Стоимость" className="finance-shell__price">{formatMoney(item.amountDue)}</td>
              <td data-label="Оплата"><FinanceBadge status={item.paymentStatus} amountDue={item.amountDue} amountPaid={item.amountPaid} /></td>
              <td data-label="Способ">{paymentMethodLabel(item.paymentMethod) || "—"}</td>
              <td data-label="Комментарий" className="finance-shell__comment-cell">{item.comment || "—"}</td>
              <td data-label="">
                <div className="finance-shell__row-actions">
                  <button type="button" className="finance-shell__icon-btn" onClick={() => openPaymentModal(item)}>
                    {compact ? "Проверить" : "Изменить"}
                  </button>
                  <button type="button" className="finance-shell__icon-btn is-ghost" onClick={() => navigate("/dashboard/calendar")}>
                    Календарь
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!compact ? (
        <div className="finance-shell__table-footer">
          <span>Показаны {tableItems.length} из {filteredHistoryItems.length} записей</span>
          <span>Используйте фильтры и экспорт CSV для сверки журнала.</span>
        </div>
      ) : null}
    </div>
  );

  const renderPsychologistMode = () => {
    if (loading) {
      return <div className="finance-shell__loading">Загружаем журнал оплат…</div>;
    }

    if (!filteredHistoryItems.length && psyTab === "empty-payments") {
      return (
        <div className="finance-shell__empty-card">
          <div className="finance-shell__empty-title">История оплат пока пуста</div>
          <div className="finance-shell__empty-text">
            Раздел начнёт наполняться по мере того, как вы будете фиксировать оплаты по записям.
          </div>
          <button type="button" className="finance-shell__btn finance-shell__btn--dark" onClick={() => setTeamTab("profile-payment-settings")}>
            Перейти к настройкам оплаты
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="finance-shell__overview-card">
          <div className="finance-shell__note">
            <div className="finance-shell__note-icon">i</div>
            <div>
              <strong>Ручной учёт оплат.</strong> Обзор показывает только главное за период: сколько оплачено и где
              нужно ваше действие.
            </div>
          </div>
          {renderSummaryStrip()}
        </div>

        {psyTab === "overview" ? (
          <div className="finance-shell__overview-grid">
            <div className="finance-shell__card finance-shell__overview-list-card">
              <div className="finance-shell__card-head">
                <div>
                  <div className="finance-shell__card-title">К оплате и проверке</div>
                  <div className="finance-shell__card-sub">Клиенты, по которым сейчас нужно действие</div>
                </div>
                <button type="button" className="finance-shell__btn finance-shell__btn--outline finance-shell__btn--sm" onClick={() => setPsyTab("needs-action")}>
                  Открыть всё
                </button>
              </div>
              <div className="finance-shell__mini-list">
                {needsActionItems.slice(0, 3).map((item) => (
                  <div className="finance-shell__mini-row" key={`need-${item.bookingId}-${item.bookingMemberId || "d"}-${item.clientUserId}`}>
                    <div className="finance-shell__mini-left">
                      <div className="finance-shell__avatar">{initials(item.clientName)}</div>
                      <div>
                        <div className="finance-shell__mini-name">{item.clientName}</div>
                        <div className="finance-shell__mini-meta">{formatDate(item.startAt, { day: "2-digit", month: "2-digit" })} · {formatDate(item.startAt, { hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                    </div>
                    <div className="finance-shell__mini-right">
                      <FinanceBadge status={item.paymentStatus} amountDue={item.amountDue} amountPaid={item.amountPaid} />
                      <div className="finance-shell__mini-amount">
                        {item.paymentStatus === "PARTIAL" ? `${formatMoney(item.amountPaid)} / ${formatMoney(item.amountDue)}` : formatMoney(item.amountDue)}
                      </div>
                    </div>
                  </div>
                ))}
                {!needsActionItems.length ? <div className="finance-shell__mini-empty">Сейчас нет записей, требующих вашего действия.</div> : null}
              </div>
              <div className="finance-shell__overview-actions">
                <button type="button" className="finance-shell__btn finance-shell__btn--mint finance-shell__btn--sm" onClick={() => needsActionItems[0] && openPaymentModal(needsActionItems[0])} disabled={!needsActionItems.length}>
                  Отметить оплату
                </button>
                <button type="button" className="finance-shell__btn finance-shell__btn--outline finance-shell__btn--sm" onClick={() => setPsyTab("history")}>
                  В историю оплат
                </button>
              </div>
            </div>

            <div className="finance-shell__card finance-shell__overview-list-card">
              <div className="finance-shell__card-head">
                <div>
                  <div className="finance-shell__card-title">Последние оплаты</div>
                  <div className="finance-shell__card-sub">Недавно подтверждённые поступления</div>
                </div>
              </div>
              <div className="finance-shell__mini-list">
                {latestPaidItems.map((item) => (
                  <div className="finance-shell__mini-row" key={`paid-${item.bookingId}-${item.bookingMemberId || "d"}-${item.clientUserId}`}>
                    <div className="finance-shell__mini-left">
                      <div className="finance-shell__avatar">{initials(item.clientName)}</div>
                      <div>
                        <div className="finance-shell__mini-name">{item.clientName}</div>
                        <div className="finance-shell__mini-meta">{paymentMethodLabel(item.paymentMethod) || "—"}</div>
                      </div>
                    </div>
                    <div className="finance-shell__mini-right">
                      <FinanceBadge status="PAID" amountDue={item.amountDue} amountPaid={item.amountPaid} />
                      <div className="finance-shell__mini-amount">{formatMoney(item.amountPaid || item.amountDue)}</div>
                    </div>
                  </div>
                ))}
                {!latestPaidItems.length ? <div className="finance-shell__mini-empty">Подтверждённых оплат пока нет.</div> : null}
              </div>
              <div className="finance-shell__overview-actions">
                <button type="button" className="finance-shell__btn finance-shell__btn--outline finance-shell__btn--sm" onClick={() => setPsyTab("history")}>
                  Смотреть всю историю
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {psyTab === "needs-action" ? (
          <>
            <div className="finance-shell__notice">
              Здесь собраны записи, по которым нужно действие: подтвердить оплату, отметить частичную оплату или
              оставить комментарий.
            </div>
            {!needsActionItems.length ? (
              <div className="finance-shell__mini-empty is-card">Сейчас нет записей, требующих проверки.</div>
            ) : (
              renderHistoryTable(needsActionItems, true)
            )}
          </>
        ) : null}

        {psyTab === "history" ? (
          <>
            <div className="finance-shell__notice">
              <strong>История оплат</strong> помогает искать, фильтровать и выгружать записи по статусам и клиентам.
            </div>
            <div className="finance-shell__filters">
              <select value={periodFilter} onChange={(event) => setPeriodFilter(event.target.value)}>
                <option value="current">Текущий месяц</option>
                <option value="last">Прошлый месяц</option>
                <option value="all">Весь период</option>
              </select>
              <select value={paymentStatusFilter} onChange={(event) => setPaymentStatusFilter(event.target.value)}>
                <option value="ALL">Оплата: все</option>
                <option value="PAID">Оплачено</option>
                <option value="PENDING_CONFIRMATION">Ожидает подтверждения</option>
                <option value="UNPAID">Не оплачено</option>
                <option value="PARTIAL">Частично</option>
                <option value="NOT_REQUIRED">Не требуется</option>
              </select>
              <select value={sessionStatusFilter} onChange={(event) => setSessionStatusFilter(event.target.value)}>
                <option value="ALL">Сессии: все</option>
                <option value="PENDING_PAYMENT">Запланирована</option>
                <option value="COMPLETED">Проведена</option>
                <option value="NO_SHOW">Неявка</option>
                <option value="CANCELLED">Отменена</option>
              </select>
              <select value={methodFilter} onChange={(event) => setMethodFilter(event.target.value)}>
                <option value="ALL">Способ: все</option>
                {PAYMENT_METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <input
                className="finance-shell__field-input"
                placeholder="Поиск по клиенту"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <button type="button" className="finance-shell__btn finance-shell__btn--outline finance-shell__btn--sm finance-shell__filters-push" onClick={() => handleExport(filteredHistoryItems)}>
                Экспорт CSV
              </button>
            </div>
            {!filteredHistoryItems.length ? (
              <div className="finance-shell__mini-empty is-card">По текущим фильтрам записей нет.</div>
            ) : (
              renderHistoryTable(filteredHistoryItems)
            )}
          </>
        ) : null}

        {psyTab === "empty-payments" ? (
          <div className="finance-shell__empty-card">
            <div className="finance-shell__empty-title">Раздел начнёт работать после первых записей</div>
            <div className="finance-shell__empty-text">
              Настройте способ прямой оплаты и правила отмены в командном режиме, чтобы фиксировать оплаты было проще.
            </div>
          </div>
        ) : null}
      </>
    );
  };

  const renderTeamMode = () => (
    <>
      {teamTab === "profile-payment-settings" ? (
        <>
          <div className="finance-shell__notice finance-shell__notice--with-icon">
            <div className="finance-shell__notice-icon">i</div>
            <div>
              Этот блок должен жить в <strong>профиле психолога</strong>, а не отдельной вкладкой внутри финансов.
              Психолог заполняет его вместе с профилем, чтобы клиент сразу видел условия записи.
            </div>
          </div>

          <div className="finance-shell__settings-grid">
            <div className="finance-shell__settings-col">
              <div className="finance-shell__card finance-shell__form-section">
                <div className="finance-shell__section-title">Стоимость консультаций</div>
                <div className="finance-shell__section-sub">Эти цены будут предложены по умолчанию при создании записи</div>
                <div className="finance-shell__form-row">
                  <label className="finance-shell__field">
                    <span>Индивидуальная</span>
                    <div className="finance-shell__price-input-wrap">
                      <input
                        className="finance-shell__field-input"
                        value={paymentSettingsForm.pricePerSession}
                        onChange={(event) => setPaymentSettingsForm((prev) => ({ ...prev, pricePerSession: event.target.value.replace(/[^\d]/g, "") }))}
                        placeholder="4000"
                        disabled={!isPsychologist || settingsSaving}
                      />
                      <em>₽</em>
                    </div>
                  </label>
                  <label className="finance-shell__field">
                    <span>Групповая</span>
                    <div className="finance-shell__price-input-wrap">
                      <input
                        className="finance-shell__field-input"
                        value={paymentSettingsForm.pricePerSession}
                        onChange={(event) => setPaymentSettingsForm((prev) => ({ ...prev, pricePerSession: event.target.value.replace(/[^\d]/g, "") }))}
                        placeholder="1800"
                        disabled={!isPsychologist || settingsSaving}
                      />
                      <em>₽</em>
                    </div>
                  </label>
                </div>
              </div>

              <div className="finance-shell__card finance-shell__form-section">
                <div className="finance-shell__section-title">Как клиент оплачивает</div>
                <div className="finance-shell__form-row">
                  <label className="finance-shell__field">
                    <span>Когда оплачивает</span>
                    <select
                      value={paymentSettingsForm.paymentTimingLabel || paymentTimingOptions[0]}
                      onChange={(event) => setPaymentSettingsForm((prev) => ({ ...prev, paymentTimingLabel: event.target.value }))}
                      disabled={!isPsychologist || settingsSaving}
                    >
                      {paymentTimingOptions.map((option) => (
                        <option key={option || "empty"} value={option}>{option || "Не указано"}</option>
                      ))}
                    </select>
                  </label>
                  <label className="finance-shell__field">
                    <span>Способ оплаты</span>
                    <select
                      value={selectedPaymentMethod}
                      onChange={(event) =>
                        setPaymentSettingsForm((prev) => ({
                          ...prev,
                          paymentMethods: event.target.value ? [event.target.value] : [],
                          paymentSelfEmployed: event.target.value === "self_employed" ? true : prev.paymentSelfEmployed,
                        }))
                      }
                      disabled={!isPsychologist || settingsSaving}
                    >
                      <option value="">Не указано</option>
                      {PAYMENT_METHOD_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="finance-shell__form-row finance-shell__form-row--single">
                  <label className="finance-shell__field">
                    <span>Инструкция для клиента</span>
                    <textarea
                      className="finance-shell__field-input is-area"
                      value={paymentSettingsForm.paymentInstructions}
                      onChange={(event) => setPaymentSettingsForm((prev) => ({ ...prev, paymentInstructions: event.target.value }))}
                      placeholder="Оплата переводом по номеру телефона после подтверждения записи. Реквизиты отправлю в чате."
                      rows={4}
                      disabled={!isPsychologist || settingsSaving}
                    />
                  </label>
                </div>
                <label className="finance-shell__check-row">
                  <input
                    type="checkbox"
                    checked={paymentSettingsForm.paymentSelfEmployed}
                    onChange={(event) => setPaymentSettingsForm((prev) => ({ ...prev, paymentSelfEmployed: event.target.checked }))}
                    disabled={!isPsychologist || settingsSaving}
                  />
                  <span>Показывать клиенту, что специалист может прислать чек или работает как самозанятый</span>
                </label>
              </div>

              <div className="finance-shell__card finance-shell__form-section">
                <div className="finance-shell__section-title">Правила отмены и возврата</div>
                <div className="finance-shell__section-sub">Клиент увидит эти условия перед подтверждением записи</div>
                <div className="finance-shell__policy-options">
                  {POLICY_PRESETS.map((option) => (
                    <label
                      key={option.id}
                      className={`finance-shell__policy-option ${activePolicyPreset === option.id ? "is-active" : ""}`.trim()}
                    >
                      <input
                        type="radio"
                        name="payment-policy"
                        checked={activePolicyPreset === option.id}
                        onChange={() => applyPolicyPreset(option.id)}
                        disabled={!isPsychologist || settingsSaving}
                      />
                      <div>
                        <div className="finance-shell__policy-title">{option.title}</div>
                        <div className="finance-shell__policy-sub">{option.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="finance-shell__form-row finance-shell__form-row--single finance-shell__form-row--spaced">
                  <label className="finance-shell__field">
                    <span>Правила возврата</span>
                    <textarea
                      className="finance-shell__field-input is-area"
                      value={paymentSettingsForm.paymentCancellationPolicy}
                      onChange={(event) => setPaymentSettingsForm((prev) => ({ ...prev, paymentCancellationPolicy: event.target.value }))}
                      placeholder="При отмене позднее установленного срока предоплата не возвращается. При переносе по инициативе специалиста сессия компенсируется полностью."
                      rows={4}
                      disabled={!isPsychologist || settingsSaving}
                    />
                  </label>
                </div>
                <div className="finance-shell__hint-box">
                  Это не платёжные настройки платформы. Это условия специалиста, которые клиент видит перед записью.
                </div>
              </div>

              {isPsychologist ? (
                <div className="finance-shell__actions-row finance-shell__actions-row--end">
                  <button type="button" className="finance-shell__btn finance-shell__btn--outline" onClick={() => setPaymentSettingsForm(createPaymentSettingsForm(profile || {}))} disabled={settingsSaving}>
                    Отмена
                  </button>
                  <button type="button" className="finance-shell__btn finance-shell__btn--mint" onClick={savePaymentSettings} disabled={settingsSaving}>
                    {settingsSaving ? "Сохраняем…" : "Сохранить настройки"}
                  </button>
                </div>
              ) : null}
            </div>

            <aside className="finance-shell__preview-col finance-shell__preview-sticky">
              <div className="finance-shell__card finance-shell__preview-card">
                <div className="finance-shell__preview-head">
                  <span className="finance-shell__preview-dot" />
                  Так это увидит клиент
                </div>
                <div className="finance-shell__terms-card finance-shell__terms-card--preview">
                  <div className="finance-shell__terms-rows">
                    <div className="finance-shell__terms-row"><span>Стоимость</span><strong className="finance-shell__terms-price">{moneyLabel(previewPriceValue)}</strong></div>
                    <div className="finance-shell__terms-row"><span>Когда оплатить</span><strong>{paymentSettingsForm.paymentTimingLabel || "Не указано"}</strong></div>
                    <div className="finance-shell__terms-row"><span>Способ оплаты</span><strong>{paymentMethodLabel(selectedPaymentMethod) || "Не указано"}</strong></div>
                  </div>
                  <div className="finance-shell__terms-copy">
                    {paymentSettingsForm.paymentInstructions || "Инструкция не указана."}
                  </div>
                  <div className="finance-shell__terms-policy">
                    {paymentSettingsForm.paymentCancellationPolicy || "Правила отмены и переноса не указаны."}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </>
      ) : null}

      {teamTab === "client-flow" ? (
        <div className="finance-shell__two-col">
          <div className="finance-shell__card finance-shell__terms-card">
            <div className="finance-shell__terms-head">
              <div className="finance-shell__terms-icon">✓</div>
              <div className="finance-shell__terms-title">До подтверждения записи</div>
            </div>
            <div className="finance-shell__terms-rows">
              <div className="finance-shell__terms-row"><span>Стоимость консультации</span><strong>{moneyLabel(previewPriceValue)}</strong></div>
              <div className="finance-shell__terms-row"><span>Когда нужно оплатить</span><strong>{paymentSettingsForm.paymentTimingLabel || "Не указано"}</strong></div>
              <div className="finance-shell__terms-row"><span>Способ оплаты</span><strong>{paymentMethodLabel(selectedPaymentMethod) || "Не указано"}</strong></div>
            </div>
            <div className="finance-shell__terms-copy">
              {paymentSettingsForm.paymentInstructions || "Инструкция не указана."}
            </div>
            <div className="finance-shell__terms-rows finance-shell__terms-rows--compact">
              <div className="finance-shell__terms-row"><span>Отмена / перенос</span><strong>{activePolicyPreset === "soft" ? "Бесплатно за 12 часов" : activePolicyPreset === "standard" ? "Бесплатно за 24 часа" : (paymentSettingsForm.paymentCancellationPolicy ? "По правилам специалиста" : "Не указано")}</strong></div>
              <div className="finance-shell__terms-row"><span>Возврат</span><strong>{paymentSettingsForm.paymentCancellationPolicy || "Не указано"}</strong></div>
            </div>
            <div className="finance-shell__direct-pay-note">
              Оплата проходит напрямую специалисту. Бюро Души не принимает деньги за консультацию.
            </div>
            <label className="finance-shell__terms-check">
              <input type="checkbox" checked readOnly />
              <span>Я ознакомился(ась) с условиями оплаты, отмены и переноса</span>
            </label>
            <button type="button" className="finance-shell__btn finance-shell__btn--mint finance-shell__btn--full">
              Подтвердить запись
            </button>
          </div>

          <div className="finance-shell__card finance-shell__terms-card">
            <div className="finance-shell__terms-head">
              <div className="finance-shell__terms-icon is-lav">✓</div>
              <div className="finance-shell__terms-title">После подтверждения записи</div>
            </div>
            <div className="finance-shell__notice">
              Запись подтверждена. Оплатите консультацию напрямую специалисту.
            </div>
            <div className="finance-shell__terms-rows">
              <div className="finance-shell__terms-row"><span>Сумма</span><strong>{moneyLabel(previewPriceValue)}</strong></div>
              <div className="finance-shell__terms-row"><span>Способ</span><strong>{paymentMethodLabel(selectedPaymentMethod) || "Не указано"}</strong></div>
              <div className="finance-shell__terms-row"><span>Инструкция</span><strong>{paymentSettingsForm.paymentInstructions || "Не указано"}</strong></div>
            </div>
            <div className="finance-shell__overview-actions finance-shell__overview-actions--split">
              <button type="button" className="finance-shell__btn finance-shell__btn--outline finance-shell__btn--sm" onClick={copyPaymentInstructions}>
                Скопировать реквизиты
              </button>
              <button type="button" className="finance-shell__btn finance-shell__btn--mint finance-shell__btn--sm" onClick={() => toast.success("Специалист увидит статус «Ожидает подтверждения»")}>
                Я оплатил(а)
              </button>
            </div>
            <div className="finance-shell__modal-note">
              После нажатия «Я оплатил(а)» статус станет «Ожидает подтверждения». Специалист подтвердит оплату вручную после проверки.
            </div>
          </div>
        </div>
      ) : null}

      {teamTab === "how-it-works" ? (
        <>
          <div className="finance-shell__info-grid">
            <div className="finance-shell__card finance-shell__info-card"><div className="finance-shell__card-title">Что делает раздел</div><div className="finance-shell__info-text">Финансы помогают специалисту вести ручной учёт оплат по сессиям: видеть стоимость, статус оплаты, способ оплаты и комментарии.</div></div>
            <div className="finance-shell__card finance-shell__info-card"><div className="finance-shell__card-title">Что не делает платформа</div><div className="finance-shell__info-text">Бюро Души не принимает оплату за консультации, не хранит средства, не показывает баланс и не делает вывод денег.</div></div>
            <div className="finance-shell__card finance-shell__info-card"><div className="finance-shell__card-title">Кто подтверждает оплату</div><div className="finance-shell__info-text">Клиент может нажать «Я оплатил(а)», но финальное подтверждение всегда делает психолог после проверки поступления.</div></div>
          </div>
          <div className="finance-shell__card finance-shell__form-section">
            <div className="finance-shell__section-title">Сценарий работы</div>
            <div className="finance-shell__logic-list">
              <div className="finance-shell__logic-item"><span>1</span>Психолог указывает цены, способ оплаты, инструкцию и правила отмены в профиле.</div>
              <div className="finance-shell__logic-item"><span>2</span>Клиент видит условия при записи и подтверждает согласие.</div>
              <div className="finance-shell__logic-item"><span>3</span>Клиент оплачивает напрямую специалисту и может нажать «Я оплатил(а)».</div>
              <div className="finance-shell__logic-item"><span>4</span>Психолог проверяет поступление и вручную ставит итоговый статус оплаты.</div>
            </div>
          </div>
        </>
      ) : null}

      {teamTab === "tech-notes" ? (
        <div className="finance-shell__table-card">
          <table className="finance-shell__table is-tech">
            <thead>
              <tr><th>Сущность / поле</th><th>Для чего нужно</th><th>Комментарий</th></tr>
            </thead>
            <tbody>
              <tr><td>payment_status</td><td>Статус оплаты</td><td>paid, pending_confirmation, unpaid, partial, not_required</td></tr>
              <tr><td>client_marked_paid_at</td><td>Клиент нажал «Я оплатил(а)»</td><td>Не равно подтверждённой оплате</td></tr>
              <tr><td>payment_confirmed_at</td><td>Психолог подтвердил оплату</td><td>Именно это считается финальным подтверждением</td></tr>
              <tr><td>payment_terms_snapshot</td><td>Условия на момент записи</td><td>Цена и правила не меняются задним числом</td></tr>
              <tr><td>payment_comment</td><td>Комментарий специалиста</td><td>Например, «оплатила заранее» или «остаток после сессии»</td></tr>
            </tbody>
          </table>
        </div>
      ) : null}

      {teamTab === "states" ? (
        <div className="finance-shell__card finance-shell__form-section">
          <div className="finance-shell__section-title">Статусы оплаты</div>
          {["PAID", "PENDING_CONFIRMATION", "UNPAID", "PARTIAL", "NOT_REQUIRED"].map((status) => (
            <div key={status} className="finance-shell__state-row">
              <FinanceBadge status={status} amountDue={4000} amountPaid={2000} />
              <div className="finance-shell__state-text">
                {status === "PAID" ? "Специалист проверил поступление и подтвердил оплату." : null}
                {status === "PENDING_CONFIRMATION" ? "Клиент нажал «Я оплатил(а)», специалист ещё не проверил." : null}
                {status === "UNPAID" ? "Оплата не поступила или не была отмечена." : null}
                {status === "PARTIAL" ? "Часть суммы оплачена, есть остаток." : null}
                {status === "NOT_REQUIRED" ? "Оплата не нужна по правилам специалиста или по ситуации." : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );

  return (
    <div className="podcasts-hub finance-page-shell">
      <div className="shell shell--shared podcasts-shell">
        <AppSidebarV2
          activeKey="finance"
          mobileOpen={sidebarOpen}
          onCloseMobile={() => setSidebarOpen(false)}
          roleLabel={me?.role || roleToken}
          userName={me?.name || "Пользователь"}
          userEmail={me?.email || ""}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <main className="main podcasts-main finance-shell">
          <div className="finance-shell__inner">
          <div className="finance-shell__head">
            <div>
              <div className="finance-shell__title">Финансы</div>
              <div className="finance-shell__subtitle">Ручной учёт оплат по сессиям</div>
            </div>
            <div className="finance-shell__head-actions">
              {mode === "psychologist" ? (
                <button type="button" className="finance-shell__btn finance-shell__btn--outline finance-shell__btn--sm" onClick={() => handleExport(filteredHistoryItems)}>
                  Экспорт CSV
                </button>
              ) : null}
              {canUseTeamMode && mode === "team" ? (
                <button
                  type="button"
                  className="finance-shell__btn finance-shell__btn--dark finance-shell__btn--sm"
                  onClick={() => setTeamTab("profile-payment-settings")}
                >
                  Оплата в профиле
                </button>
              ) : null}
            </div>
          </div>

          {canUseTeamMode ? (
            <div className="finance-shell__mode-switch" aria-label="Режим просмотра">
              <button
                type="button"
                className={`finance-shell__mode-btn ${mode === "psychologist" ? "is-active" : ""}`.trim()}
                onClick={() => setMode("psychologist")}
              >
                Интерфейс психолога
              </button>
              <button
                type="button"
                className={`finance-shell__mode-btn ${mode === "team" ? "is-active" : ""}`.trim()}
                onClick={() => setMode("team")}
              >
                Для команды
              </button>
            </div>
          ) : null}

          {mode === "psychologist" && canUsePsychologistMode ? (
            <>
              <div className="finance-shell__subnav">
                {PSY_TAB_IDS.map((tabId) => (
                  <button
                    type="button"
                    key={tabId}
                    className={`finance-shell__subnav-btn ${psyTab === tabId ? "is-active" : ""}`.trim()}
                    onClick={() => setPsyTab(tabId)}
                  >
                    {tabId === "overview" ? "Обзор" : null}
                    {tabId === "needs-action" ? "К оплате" : null}
                    {tabId === "history" ? "История оплат" : null}
                    {tabId === "empty-payments" ? "Пустое состояние" : null}
                  </button>
                ))}
              </div>
              {renderPsychologistMode()}
            </>
          ) : null}

          {mode === "team" && canUseTeamMode ? (
            <>
              <div className="finance-shell__subnav">
                {TEAM_TAB_IDS.map((tabId) => (
                  <button
                    type="button"
                    key={tabId}
                    className={`finance-shell__subnav-btn ${teamTab === tabId ? "is-active" : ""}`.trim()}
                    onClick={() => setTeamTab(tabId)}
                  >
                    {tabId === "profile-payment-settings" ? "Профиль: оплата" : null}
                    {tabId === "client-flow" ? "Клиентский сценарий" : null}
                    {tabId === "how-it-works" ? "Как работает" : null}
                    {tabId === "tech-notes" ? "Тех. заметки" : null}
                    {tabId === "states" ? "Состояния" : null}
                  </button>
                ))}
              </div>
              {renderTeamMode()}
            </>
          ) : null}
          </div>
        </main>
      </div>

      <Modal
        open={Boolean(modalItem)}
        onClose={closePaymentModal}
        title="Отметить оплату"
        size="md"
        className="finance-modal"
        actions={(
          <>
            <button type="button" className="finance-shell__btn finance-shell__btn--outline" onClick={closePaymentModal} disabled={saving}>
              Отмена
            </button>
            <button type="button" className="finance-shell__btn finance-shell__btn--mint" onClick={savePayment} disabled={saving}>
              {saving ? "Сохраняем…" : "Сохранить"}
            </button>
          </>
        )}
      >
        {modalItem ? (
          <div className="finance-shell__modal-body">
            <div className="finance-shell__modal-sub">
              {modalItem.clientName} · {formatDateTime(modalItem.startAt)} · {formatMoney(modalItem.amountDue)}
            </div>
            <label className="finance-shell__field">
              <span>Сумма оплаты</span>
              <input
                className="finance-shell__field-input"
                value={paymentForm.amountPaid}
                onChange={(event) => setPaymentForm((prev) => ({ ...prev, amountPaid: event.target.value.replace(/[^\d.,]/g, "").replace(",", ".") }))}
              />
            </label>
            <label className="finance-shell__field">
              <span>Дата оплаты</span>
              <input
                className="finance-shell__field-input"
                type="date"
                value={paymentForm.paymentDate}
                onChange={(event) => setPaymentForm((prev) => ({ ...prev, paymentDate: event.target.value }))}
              />
            </label>
            <label className="finance-shell__field">
              <span>Статус оплаты</span>
              <select value={paymentForm.paymentStatus} onChange={(event) => setPaymentForm((prev) => ({ ...prev, paymentStatus: event.target.value }))}>
                <option value="PAID">Оплачено</option>
                <option value="PARTIAL">Частично оплачено</option>
                <option value="PENDING_CONFIRMATION">Ожидает подтверждения</option>
                <option value="UNPAID">Не оплачено</option>
                <option value="NOT_REQUIRED">Не требуется</option>
              </select>
            </label>
            <label className="finance-shell__field">
              <span>Способ оплаты</span>
              <select value={paymentForm.paymentMethod} onChange={(event) => setPaymentForm((prev) => ({ ...prev, paymentMethod: event.target.value }))}>
                {PAYMENT_METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="finance-shell__field">
              <span>Комментарий</span>
              <textarea
                className="finance-shell__field-input is-area"
                value={paymentForm.comment}
                onChange={(event) => setPaymentForm((prev) => ({ ...prev, comment: event.target.value }))}
                rows={4}
                placeholder="Необязательно"
              />
            </label>
            <div className="finance-shell__modal-note">
              Эта отметка нужна для вашего учёта. Бюро Души не участвует в расчётах между клиентом и психологом.
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
